import { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Wand2, ArrowUpRight, Filter } from 'lucide-react';
import { usePlanEditor } from './PlanEditorContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLASS_LABELS = {
  wall_load_bearing: { label: 'Mur porteur', color: '#f87171' },
  wall: { label: 'Mur', color: '#fbbf24' },
  partition: { label: 'Cloison', color: '#9ca3af' },
  thin: { label: 'Trait fin', color: '#6b7280' },
};

// Classification Lab → types de murs de l'éditeur Structure.
const CLASS_TO_WALL_TYPE = {
  wall_load_bearing: 'porteur',
  wall: 'porteur',
  partition: 'cloison_fine',
  thin: 'cloison_fine',
};

const Slider = ({ label, value, onChange, min, max, step, fmt, hint, testid }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <label className="text-[11px] text-gray-300">{label}</label>
      <span className="text-[11px] font-semibold text-[#D9A05B] hud-tabular" data-testid={`${testid}-value`}>
        {fmt ? fmt(value) : value}
      </span>
    </div>
    <input type="range" min={min} max={max} step={step} value={value}
           onChange={(e) => onChange(parseFloat(e.target.value))}
           className="w-full accent-[#D9A05B]" data-testid={testid} />
    {hint && <p className="text-[9.5px] text-gray-500 mt-0.5">{hint}</p>}
  </div>
);

/**
 * SubTabVectorize — V14.70 · Station 1e (Préparation).
 * Moteur déterministe du Lab (approxPolyDP + classification par épaisseur)
 * promu en production, avec filtre anti-parasites. L'extraction sauvegarde
 * automatiquement l'image vectorisée (= snapshot de fin de phase Préparation)
 * et alimente l'add-on IA utilisé par « Détecter les Murs » en 2·Structure.
 */
const SubTabVectorize = ({ projectId, selectedLotId, savedState, onSavedStateChanged, onSent }) => {
  const { walls, actions } = usePlanEditor();
  const [eps, setEps] = useState(2.5);
  const [minAreaPct, setMinAreaPct] = useState(0.08);
  const [minLen, setMinLen] = useState(12);
  const [pruneIsolated, setPruneIsolated] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [sendChoice, setSendChoice] = useState(false);
  const [sending, setSending] = useState(false);

  const persisted = savedState?.vectorization;
  const stats = result?.stats || (persisted?.engine === 'lab_v2' ? persisted?.stats : null);
  const segments = result?.segments || (persisted?.engine === 'lab_v2' ? persisted?.segments : null);
  const imageSize = result?.image || (persisted?.engine === 'lab_v2' ? persisted?.image : null);
  const hasSource = !!savedState?.source_file_id;

  const extract = useCallback(async () => {
    setBusy(true);
    try {
      const url = `${API}/projects/${projectId}/plan-source/vectorize${selectedLotId ? `?lot_id=${selectedLotId}` : ''}`;
      const r = await axios.post(url, {
        approx_eps: eps,
        min_area_ratio: minAreaPct / 100,
        min_seg_len_px: minLen,
        prune_isolated: pruneIsolated,
        include_previews: true,
      }, { withCredentials: true });
      setResult(r.data);
      toast.success(`${r.data.stats.segments} segments extraits · ${r.data.stats.filtered_out} parasites supprimés`);
      onSavedStateChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la vectorisation');
    } finally {
      setBusy(false);
    }
  }, [projectId, selectedLotId, eps, minAreaPct, minLen, pruneIsolated, onSavedStateChanged]);

  const wallsFromSegments = useMemo(() => {
    if (!segments || !imageSize?.width) return [];
    const { width, height } = imageSize;
    return segments.map((s) => ({
      x1: Math.max(0, Math.min(1, s.start[0] / width)),
      y1: Math.max(0, Math.min(1, s.start[1] / height)),
      x2: Math.max(0, Math.min(1, s.end[0] / width)),
      y2: Math.max(0, Math.min(1, s.end[1] / height)),
      type: CLASS_TO_WALL_TYPE[s.class] || 'cloison_fine',
    }));
  }, [segments, imageSize]);

  const doSend = useCallback(async (replace) => {
    setSending(true);
    try {
      actions.replaceFromDetection(wallsFromSegments, { replace });
      toast.success(`${wallsFromSegments.length} murs ${replace ? 'remplacés' : 'ajoutés'} au tracé`);
      setSendChoice(false);
      onSent?.();
    } finally {
      setSending(false);
    }
  }, [actions, wallsFromSegments, onSent]);

  const handleSend = useCallback(() => {
    if (wallsFromSegments.length === 0) return;
    // Choix utilisateur : AJOUTER par défaut, confirmation de remplacement
    // seulement si des murs existent déjà.
    if (walls.length > 0) setSendChoice(true);
    else doSend(false);
  }, [wallsFromSegments.length, walls.length, doSend]);

  return (
    <div className="space-y-3" data-testid="subtab-vectorize">
      <div>
        <h4 className="text-[12px] font-semibold flex items-center gap-1.5">
          <Wand2 className="w-3.5 h-3.5 text-[#D9A05B]" /> Vectorisation déterministe
        </h4>
        <p className="text-[10.5px] text-gray-500 mt-0.5 leading-snug">
          Moteur OpenCV : morphologie + squelette + approxPolyDP. Les segments
          sont envoyés dans le tracé et améliorent aussi « Détecter les Murs (IA) ».
        </p>
      </div>

      <Slider label="Simplification (approxPolyDP ε)" value={eps} onChange={setEps}
              min={0.5} max={10} step={0.1} fmt={(v) => v.toFixed(1)}
              hint="Plus haut = moins de points, lignes plus droites"
              testid="vec-eps-slider" />
      <Slider label="Surface min. (% total)" value={minAreaPct} onChange={setMinAreaPct}
              min={0.01} max={1} step={0.01} fmt={(v) => `${v.toFixed(2)} %`}
              hint="Filtre le texte / mobilier / annotations"
              testid="vec-minarea-slider" />
      <Slider label="Longueur min. (px) — anti-parasites" value={minLen} onChange={setMinLen}
              min={0} max={60} step={1} fmt={(v) => `${v} px`}
              hint="Supprime les micro-segments parasites"
              testid="vec-minlen-slider" />
      <button
        type="button"
        onClick={() => setPruneIsolated((v) => !v)}
        className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded border text-[11px] transition-colors ${
          pruneIsolated
            ? 'bg-[#D9A05B]/15 border-[#D9A05B]/40 text-[#D9A05B]'
            : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
        }`}
        data-testid="vec-prune-toggle"
      >
        <Filter className="w-3 h-3" />
        Supprimer les vecteurs isolés {pruneIsolated ? 'ON' : 'OFF'}
      </button>

      <button
        type="button"
        onClick={extract}
        disabled={busy || !hasSource}
        className="w-full h-9 rounded-lg font-semibold text-[12px] text-black disabled:opacity-40 disabled:cursor-not-allowed"
        style={{ background: 'linear-gradient(135deg, #D9A05B, #b8823f)' }}
        data-testid="vec-extract-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin inline mr-1.5" /> : null}
        {busy ? 'Extraction…' : 'Extraire la géométrie'}
      </button>
      {!hasSource && (
        <p className="text-[10px] text-amber-400/80">Uploadez d'abord un plan (1a).</p>
      )}

      {stats && (
        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-2.5 space-y-1.5"
             data-testid="vec-stats">
          <p className="text-[11px] font-semibold text-white">
            {stats.segments} segments extraits
            {stats.filtered_out > 0 && (
              <span className="text-gray-500 font-normal"> · {stats.filtered_out} parasites filtrés</span>
            )}
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            {Object.entries(stats.by_class || {}).filter(([, n]) => n > 0).map(([cls, n]) => (
              <div key={cls} className="flex items-center justify-between px-2 py-1 rounded bg-white/[0.04] text-[10.5px]">
                <span style={{ color: CLASS_LABELS[cls]?.color }}>{CLASS_LABELS[cls]?.label || cls}</span>
                <span className="font-semibold text-white hud-tabular">{n}</span>
              </div>
            ))}
          </div>
          {result?.previews && (
            <div className="grid grid-cols-3 gap-1.5 pt-1">
                {[['binarized_b64', 'Binarisation'], ['wall_mask_b64', 'Masque murs'], ['skeleton_b64', 'Squelette']].map(([k, lbl]) => {
                const raw = result.previews[k];
                if (!raw) return null;
                const src = raw.startsWith('data:') ? raw : `data:image/png;base64,${raw}`;
                return (
                  <div key={k}>
                    <img src={src} alt={lbl}
                         className="w-full h-14 object-contain bg-white rounded border border-white/10" />
                    <p className="text-[9px] text-gray-500 text-center mt-0.5">{lbl}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {segments && segments.length > 0 && (
        <div className="space-y-1.5">
          {!sendChoice ? (
            <button
              type="button"
              onClick={handleSend}
              disabled={sending}
              className="w-full h-8 rounded-lg text-[11.5px] font-medium border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40"
              data-testid="vec-send-structure-btn"
            >
              <ArrowUpRight className="w-3.5 h-3.5 inline mr-1" />
              Envoyer {segments.length} vecteurs vers 2·Structure
            </button>
          ) : (
            <div className="rounded-lg border border-amber-400/40 bg-amber-500/10 p-2 space-y-1.5"
                 data-testid="vec-send-choice">
              <p className="text-[10.5px] text-amber-200">
                {walls.length} mur{walls.length > 1 ? 's' : ''} déjà présent{walls.length > 1 ? 's' : ''} en Structure :
              </p>
              <div className="flex items-center gap-1.5">
                <button type="button" onClick={() => doSend(false)} disabled={sending}
                        className="flex-1 h-7 rounded text-[10.5px] font-medium bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="vec-send-add-btn">
                  Ajouter
                </button>
                <button type="button" onClick={() => doSend(true)} disabled={sending}
                        className="flex-1 h-7 rounded text-[10.5px] font-medium bg-rose-600 hover:bg-rose-700 text-white"
                        data-testid="vec-send-replace-btn">
                  Remplacer
                </button>
                <button type="button" onClick={() => setSendChoice(false)}
                        className="h-7 px-2 rounded text-[10.5px] border border-white/15 text-gray-300"
                        data-testid="vec-send-cancel-btn">
                  Annuler
                </button>
              </div>
            </div>
          )}
          <p className="text-[9.5px] text-gray-500 leading-snug">
            porteur → Mur porteur · mur/cloison → Cloison fine. Éditables ensuite dans 2b·Murs.
          </p>
        </div>
      )}
    </div>
  );
};

export default SubTabVectorize;
